'use client';
import { type Position } from '@capsicum/common';
import { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import { match } from 'ts-pattern';

import { Divider } from '../../../atoms';
import { MenuItem } from '../../../atoms/menu-item/MenuItem';
import BaseSearchInput from '../search-input/BaseSearchInput';

export type BaseSelectModalProps<T> = {
	customLabelProp?: (countryIso: T) => React.ReactNode;
	data: Array<T>;
	disabled?: boolean;
	emptyListMessage?: string;
	filterSearchItems?: (values: Array<T>, selectedValue?: null | T) => Array<T>;
	isOpen?: boolean;
	itemTemplate: (params: { item: T }) => React.ReactNode;
	itemTextEllipsis?: boolean;
	label?: string;
	labelProp?: keyof T;
	onChange?: (value: T) => void;
	onClose?: () => void;
	onOpen?: () => void;
	onSearch: (items: Array<T>, text: string) => Array<T>;
	readonly?: boolean;
	searchPlaceholder?: string;
	supportingText?: string;
	testID: string;
	title?: string;
	useDesktopModal?: boolean;
	value?: null | T;
	withExtraBottomSheetPadding?: boolean;
	withSearch?: boolean;
};

export const BaseSelectModal = <T,>({
	data,
	emptyListMessage,
	filterSearchItems,
	itemTemplate,
	itemTextEllipsis,
	onChange,
	onClose,
	onSearch,
	searchPlaceholder,
	testID,
	title,
	value,
	withExtraBottomSheetPadding,
	withSearch = true
}: BaseSelectModalProps<T>) => {
	const [search, setSearch] = useState<string>('');

	const searchItems = useMemo(() => onSearch(data, search), [data, search, onSearch]);

	const listItems = useMemo(
		() => (filterSearchItems ? filterSearchItems(searchItems, value) : searchItems),
		[searchItems, value, filterSearchItems]
	);

	const stopDragPropagation = useCallback((ev: React.SyntheticEvent) => {
		ev.stopPropagation();
	}, []);

	const handleSearch = useCallback(
		(text: string) => {
			setSearch(text);
		},
		[setSearch]
	);

	const onItemPress = useCallback(
		(item: T) => {
			onChange?.(item);
			onClose?.();
		},
		[onClose, onChange]
	);

	const renderItem = useCallback(
		(item: T, index: number, selected?: boolean) => (
			<MenuItem
				key={`${testID}-${index}`}
				selected={selected}
				testID={`${testID}-item--${index}`}
				textEllipsis={itemTextEllipsis}
				title={itemTemplate({ item })}
				onClick={() => onItemPress(item)}
			/>
		),
		[testID, itemTemplate, onItemPress, itemTextEllipsis]
	);

	return (
		<S.BottomSheetContainer $extraPadding={withExtraBottomSheetPadding}>
			<S.BottomSheetTitle>{title}</S.BottomSheetTitle>
			{withSearch && (
				<BaseSearchInput
					autoFocus
					placeholder={searchPlaceholder}
					testID={`${testID}-mobile-input`}
					value={search}
					onChange={e => handleSearch(e.target.value)}
				/>
			)}
			{searchItems.length === 0 && emptyListMessage && <S.EmptyText>{emptyListMessage}</S.EmptyText>}

			{match(listItems.length).otherwise(() => (
				<S.BaseSelectList
					data-testid={`${testID}-list`}
					onPointerDownCapture={stopDragPropagation}
					onTouchStartCapture={stopDragPropagation}
				>
					{value && (
						<S.BaseSelectListItem data-testid={`${testID}-item`} key="selected-item">
							<>
								{renderItem(value, -1, true)}
								<Divider orientation="horizontal" />
							</>
						</S.BaseSelectListItem>
					)}
					{listItems.map((item, index) => (
						<S.BaseSelectListItem data-testid={`${testID}-item`} key={index}>
							<>
								{renderItem(item, index)}
								{index + 1 < listItems.length && <Divider orientation="horizontal" />}
							</>
						</S.BaseSelectListItem>
					))}
				</S.BaseSelectList>
			))}
		</S.BottomSheetContainer>
	);
};

namespace S {
	export const BaseSelectListItem = styled.li<{ $selected?: boolean }>`
		${({ theme }) => theme.fonts.text.text3};

		&:hover {
			cursor: pointer;
			color: ${({ theme }) => theme.colors.text.onDark.enabled};
			background-color: ${({ theme }) => theme.colors.text.main.enabled};
		}
	`;

	export const ItemWrapper = styled(MenuItem)`
		padding: ${({ theme }) => theme.spacings.medium};
	`;

	export const EmptyListWrapper = styled.div<{ $minWidth: number; $position: Position }>`
		position: absolute;
		overflow-y: scroll;
		top: ${({ $position }) => $position.y}px;
		left: ${({ $position }) => $position.x}px;
		background-color: ${({ theme }) => theme.colors.backgrounds.main.container};
		padding: ${({ theme }) => theme.spacings.xsmall};
		margin: 0;
		${({ theme }) => theme.border.radius.medium}
		${({ theme }) => theme.elevations.level1}
		z-index: 11;
		width: fit-content;
		min-width: ${({ $minWidth }) => $minWidth}px;
		max-width: 500px;
		max-height: 254px;
	`;

	export const EmptyText = styled.p`
		${({ theme }) => theme.fonts.text.text2};
	`;

	export const BottomSheetContainer = styled.div<{ $extraPadding?: boolean }>`
		padding: ${({ $extraPadding, theme }) =>
			$extraPadding ? `${theme.spacings.small} ${theme.spacings.large}` : 0};
	`;

	export const BottomSheetTitle = styled.p`
		${({ theme }) => theme.fonts.headings.title6};
		color: ${({ theme }) => theme.colors.text.main.enabled};
	`;

	export const BaseSelectList = styled.div`
		overflow-y: scroll;
		overflow-x: hidden;
		height: 300px;
		margin-top: ${({ theme }) => theme.spacings.small};
		list-style-type: none;
	`;
	export const BottomWrapper = styled.div<{ $isRTL: boolean }>`
		display: flex;
	`;

	export const SupportingInputInfo = styled.span<{ $disabled?: boolean; $hasError: boolean }>`
		${({ theme }) => theme.fonts.text.caption2};
		color: ${({ $disabled, $hasError, theme }) => {
			if ($disabled) return theme.colors.text.main.disabled;
			if ($hasError) return theme.colors.borders.error.enabled;

			return theme.colors.text.main.enabled;
		}};
	`;

	export const SupportingText = styled(SupportingInputInfo)`
		align-self: flex-start;
		flex-grow: 2;
	`;
}
